import type { ExternalUrlDefinition, RouteDefinition } from './types'

function defineRoute<TPath extends string>(
  path: TPath,
  category: RouteDefinition['category'],
  description?: string
): RouteDefinition<TPath> {
  return { path, category, description } as const
}

function defineExternalUrl(
  url: string,
  description?: string
): ExternalUrlDefinition {
  return { url, category: 'external-url', description } as const
}

export const routes = {
  home: defineRoute('/', 'internal', 'Homepage'),
  features: defineRoute('/features', 'internal', 'Features page'),
  mcp: defineRoute('/mcp', 'internal', 'Super MCP page'),
  agent: defineRoute('/slite-agent', 'internal', 'Slite Agent page'),
  browserExtension: defineRoute(
    '/browser-extension',
    'internal',
    'Browser extension page'
  ),
  digests: defineRoute('/digests', 'internal', 'Digests page'),
  askInBulk: defineRoute('/ask-in-bulk', 'internal', 'Ask in bulk page'),
  aiSearch: defineRoute('/ai-search', 'internal', 'AI search page'),
  aiTransformation: defineRoute(
    '/ai-transformation',
    'internal',
    'AI transformation leaders landing page (ICP test, paid traffic)'
  ),
  pricing: defineRoute('/pricing', 'internal', 'Pricing page'),
  download: defineRoute('/download', 'internal', 'Download page'),
  editor: defineRoute('/editor', 'internal', 'Editor demo page'),
  changelog: {
    index: defineRoute('/changelog', 'internal', 'Changelog page'),
    detail: defineRoute(
      '/changelog/:slug',
      'internal',
      'Changelog entry detail'
    ),
  },
  bookDemo: defineRoute('/book-demo', 'internal', 'Book demo page'),
  newsletter: defineRoute('/newsletter', 'internal', 'Newsletter signup page'),
  resources: defineRoute('/resources', 'internal', 'Resources hub'),
  enterpriseSearchReport: defineRoute(
    '/enterprise-search-report',
    'internal',
    'Enterprise search report'
  ),
  ebooks: {
    companyBrain: defineRoute(
      '/ebooks/company-brain',
      'internal',
      'Company Brain ebook landing page'
    ),
    companyBrainDownload: defineRoute(
      '/ebooks/company-brain/download',
      'internal',
      'Company Brain ebook download page (ungated, noindex)'
    ),
    companyBrainContent: defineRoute(
      '/ebooks/company-brain/content',
      'internal',
      'Company Brain interactive ebook (gated, noindex)'
    ),
    companyBrainContentChapter: defineRoute(
      '/ebooks/company-brain/content/:chapter',
      'internal',
      'Company Brain ebook chapter (gated, noindex)'
    ),
  },
  blog: {
    index: defineRoute('/blog', 'internal', 'Blog index'),
    post: defineRoute('/blog/:slug', 'internal', 'Blog post'),
    agentAnnouncement: defineRoute(
      '/blog/slite-announcing-self-maintaining-knowledge-base',
      'internal',
      'Slite Agent announcement post'
    ),
  },
  blogCategory: defineRoute(
    '/blog-category',
    'internal',
    'Blog category index'
  ),
  templates: {
    index: defineRoute('/templates', 'internal', 'Templates index'),
    detail: defineRoute('/templates/:slug', 'internal', 'Template detail'),
  },
  workflows: {
    index: defineRoute('/workflows', 'internal', 'Workflows index'),
    detail: defineRoute('/workflows/:slug', 'internal', 'Workflow detail'),
  },
  integrations: {
    index: defineRoute('/integrations', 'internal', 'Integrations index'),
    documentIntegrations: defineRoute(
      '/integrations/document-integrations',
      'internal',
      'Document integrations bucket'
    ),
    sources: defineRoute('/integrations/sources', 'internal', 'Sources bucket'),
    clients: defineRoute('/integrations/clients', 'internal', 'Clients bucket'),
    detail: defineRoute(
      '/integrations/:slug',
      'internal',
      'Integration detail'
    ),
  },
  solutions: {
    index: defineRoute('/solutions', 'internal', 'Solutions landing'),
    // Shared by team solutions AND use cases — both resolve through the
    // /solutions/:slug catch-all, which determines the type and renders
    // accordingly. (Not named `useCase*` so it doesn't trip the react-hooks
    // lint rule when called inside callbacks.)
    detail: defineRoute('/solutions/:slug', 'internal', 'Solution detail'),
    knowledgeBase: defineRoute(
      '/solutions/knowledge-base',
      'internal',
      'Knowledge base solution'
    ),
    itOperations: defineRoute(
      '/solutions/it-operations',
      'internal',
      'IT Operations solution'
    ),
    productEngineering: defineRoute(
      '/solutions/product-engineering',
      'internal',
      'Product Engineering solution'
    ),
    humanResources: defineRoute(
      '/solutions/human-resources',
      'internal',
      'Human Resources solution'
    ),
    customerSupport: defineRoute(
      '/solutions/customer-support',
      'internal',
      'Customer Support solution'
    ),
    sales: defineRoute('/solutions/sales', 'internal', 'Sales solution'),
  },
  helpCenter: {
    index: defineRoute('/help', 'internal', 'Help center home'),
    article: defineRoute('/help/:id/:slug', 'internal', 'Help center article'),
  },
  learn: {
    index: defineRoute('/learn', 'internal', 'Learn index'),
    detail: defineRoute('/learn/:slug', 'internal', 'Learn article'),
  },
  webinars: {
    index: defineRoute('/webinars', 'internal', 'Webinars index'),
    detail: defineRoute('/webinars/:slug', 'internal', 'Webinar'),
  },
  microApps: {
    documentFormatter: defineRoute(
      '/micro-apps/document-formatter',
      'internal',
      'Document formatter'
    ),
    documentFormatterDoc: defineRoute(
      '/micro-apps/document-formatter/:docSlug',
      'internal',
      'Document formatter with type'
    ),
  },
  jobs: defineRoute('/jobs', 'internal', 'Jobs page'),
  ask: defineRoute('/ask', 'internal', 'Ask AI page'),
  compare: {
    index: defineRoute('/compare', 'internal', 'Compare index'),
    detail: defineRoute('/compare/:slug', 'internal', 'Compare detail'),
  },

  about: defineRoute('/about', 'internal', 'About page'),

  customers: {
    index: defineRoute('/customers', 'internal', 'Customers page'),
    detail: defineRoute('/customers/:slug', 'internal', 'Customer story'),
  },
  auth: {
    signup: defineRoute('/organization/create', 'external-app', 'Sign up'),
    signin: defineRoute('/organization/select', 'external-app', 'Sign in'),
  },
  organization: defineRoute(
    '/organization',
    'external-app',
    'Organization routes'
  ),
  api: defineRoute('/api', 'external-app', 'API routes'),
  legal: {
    sitemap: defineRoute('/sitemap.xml', 'legal', 'Sitemap'),
    legalInformation: defineRoute('/legal', 'legal', 'Legal information'),
    privacy: defineRoute('/privacy', 'legal', 'Privacy policy'),
    security: defineRoute('/security', 'legal', 'Security page'),
    terms: defineRoute('/terms', 'legal', 'Terms of service'),
    customerTerms: defineRoute('/customer-terms', 'legal', 'Customer terms'),
  },
  external: {
    appleAppStore: defineExternalUrl(
      'https://apps.apple.com/us/app/slite/id1342934691',
      'iOS App Store'
    ),
    androidAppStore: defineExternalUrl(
      'https://play.google.com/store/apps/details?id=com.slite.mobile',
      'Google Play Store'
    ),
    agentChromeExtension: defineExternalUrl(
      'https://chromewebstore.google.com/detail/slite-agent-ai-search-on/omlhdlhgiaabehoojbfbnicimdjbdonk',
      'Slite Agent Chrome extension'
    ),
    draftChromeExtension: defineExternalUrl(
      'https://chromewebstore.google.com/detail/draft-by-slite/ljkidlijlaapmiilabpldhmhekeionfh',
      'Draft by Slite Chrome extension'
    ),
    macDownload: defineExternalUrl(
      'https://download.slite.com/mac',
      'Mac download'
    ),
    windowsDownload: defineExternalUrl(
      'https://download.slite.com/windows',
      'Windows download'
    ),
    helpCenter: defineExternalUrl('https://slite.com/help', 'Help center'),
    mcpHelp: defineExternalUrl(
      'https://slite.com/help/lmeen-YwXupV23/Slite-MCP',
      'MCP help article'
    ),
    twitter: defineExternalUrl('https://x.com/SliteHQ', 'Twitter/X'),
    linkedin: defineExternalUrl(
      'https://www.linkedin.com/company/slitehq/',
      'LinkedIn'
    ),
    community: defineExternalUrl(
      'https://join.slack.com/t/slitebetacommunity/shared_invite/zt-2f38u5l4w-iYR2H_u4uiw4G982VjLxEA',
      'Slack community'
    ),
    g2: defineExternalUrl(
      'https://www.g2.com/products/slite/reviews',
      'G2 reviews'
    ),
    capterra: defineExternalUrl(
      'https://www.capterra.com/p/171199/Slite/',
      'Capterra'
    ),
    productHunt: defineExternalUrl(
      'https://www.producthunt.com/products/slite',
      'Product Hunt'
    ),
    pressKit: defineExternalUrl(
      'https://drive.google.com/drive/mobile/folders/123q_H2LlkvcSlSXEAykcOTxQABQg09v_?usp=sharing',
      'Press kit'
    ),
    appBilling: defineExternalUrl(
      'https://slite.com/api/redirect/settings/organization/billing',
      'App billing'
    ),
    superHome: defineExternalUrl('https://super.work', 'Super homepage'),
    superDemo: defineExternalUrl('https://super.work/book-demo', 'Super demo'),
    superAdsDemo: defineExternalUrl(
      'https://super.work/ads-book-demo',
      'Super ads demo'
    ),
  },
} as const

// Type for the entire routes object
export type Routes = typeof routes

/**
 * Helper to build template iframe URL (external, with dynamic ID)
 */
export function getTemplateIframeUrl(id: string): string {
  return `https://sliteuniverse.slite.com/api/s/${id}?copy=none`
}
